import type { AbstractDomainConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/abstract-domain-config'
import type { PermissionConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/permission-config'
import type { Instance } from '@empathybroker/tsclient-play/dist/models/instance/instance'
import type { FeatureConfig, FeatureNode, FeaturesConfig, FeaturesTree } from '../models'

// @ts-strict-ignore
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'

import { PermissionLevelEnum } from '@empathybroker/tsclient-play/dist/enums/permission-level.enum'
import { CONTAINER } from '../config'

import { TYPES } from '../config/inversify.types'

import { FeatureIdEnum } from '../enums'

import { ROUTER_CONSTS } from '../router/router.consts'
import { ArrayUtils } from './array.utils'

export class FeaturesTreeUtils {
  static getFeaturesTree(instanceConfig: Instance): FeaturesTree {
    const featuresConfig = CONTAINER.get<FeaturesConfig>(TYPES.FeaturesConfig)
    const featuresTree = Object.entries(instanceConfig.domains).reduce(
      (featuresTree: FeaturesTree, [domain, domainConfig]: [DomainEnum, AbstractDomainConfig]) => {
        if (featuresConfig[domain]) {
          featuresTree[domain] = FeaturesTreeUtils.getFeatureNodesFromPermissions(
            domainConfig.permissions,
            featuresConfig[domain],
          )
        }
        return featuresTree
      },
      {},
    )

    return featuresTree[DomainEnum.BROWSE]?.length
      ? FeaturesTreeUtils.getFeaturesTreeBrowse(featuresTree)
      : FeaturesTreeUtils.getFeaturesTreeGlobal(featuresTree)
  }

  // TODO - REMOVE THIS METHOD WHEN REMOVE DOMAIN MODELS
  private static getFeaturesTreeBrowse(featuresTree: FeaturesTree): FeaturesTree {
    const featureNodesBrowse = featuresTree[DomainEnum.BROWSE]
    const featureNodes = featuresTree[DomainEnum.SEARCH]

    const navigationMetrics = featureNodesBrowse?.find(
      nodeBrowse => nodeBrowse.id === FeatureIdEnum.INSIGHTS,
    )?.children[0]

    const browseSection = featureNodesBrowse?.find(
      nodeBrowse => nodeBrowse.id === FeatureIdEnum.PLAY,
    )

    if (featureNodes && navigationMetrics) {
      featureNodes.forEach(node => {
        if (node.id === FeatureIdEnum.INSIGHTS) {
          if (
            !node.children.find(
              nodeChildren =>
                nodeChildren.routeName ===
                ROUTER_CONSTS.routes[FeatureIdEnum.OVERALL][DomainEnum.BROWSE],
            )
          ) {
            node.children.push(navigationMetrics)
          }
        }
      })

      if (browseSection) {
        if (!featureNodes.find(node => node.id === FeatureIdEnum.BROWSE)) {
          featureNodes.push({
            children: browseSection.children,
            id: FeatureIdEnum.BROWSE,
            parentId: browseSection.parentId,
            permissionLevel: browseSection.permissionLevel,
          })
        }
      }
    }
    return FeaturesTreeUtils.getFeaturesTreeGlobal(featuresTree)
  }

  // TODO - REMOVE THIS METHOD WHEN REMOVE DOMAIN MODELS
  private static getFeaturesTreeGlobal(featuresTree: FeaturesTree): FeaturesTree {
    const featureNodesGlobal = featuresTree[DomainEnum.GLOBAL]
    const featureNodes = featuresTree[DomainEnum.SEARCH]

    const facets = featureNodesGlobal?.find(nodeBrowse => nodeBrowse.id === FeatureIdEnum.PLAY)
      ?.children[0]

    if (featureNodes && facets) {
      featureNodes.forEach(node => {
        if (node.id === FeatureIdEnum.PLAY) {
          if (
            !node.children.find(
              nodeChildren =>
                nodeChildren.routeName === ROUTER_CONSTS.routes[FeatureIdEnum.FACETS].list,
            )
          ) {
            node.children.push(facets)
          }
        }
      })
    }

    if (featureNodesGlobal) {
      featuresTree[DomainEnum.GLOBAL] = featureNodesGlobal.filter(
        nodeBrowse => nodeBrowse.id !== FeatureIdEnum.PLAY,
      )
    }

    return featuresTree
  }

  static hasFeatureNodePermission(
    featureId: FeatureIdEnum | string,
    featureNodes: FeatureNode[] = [],
  ): boolean {
    const node = FeaturesTreeUtils.getFeatureNode(featureId, featureNodes)
    return !!node && node.permissionLevel !== PermissionLevelEnum.NONE
  }

  static getFeatureNode(
    featureId: FeatureIdEnum | string,
    featureNodes: Array<FeatureNode> = [],
  ): FeatureNode {
    return ArrayUtils.findInHierarchy(featureId, featureNodes).pop()
  }

  private static getFeatureNodesFromPermissions(
    permissions: PermissionConfig[],
    featureConfigs: FeatureConfig[],
    parentId?: string,
  ): FeatureNode[] {
    return permissions.reduce((featureNodes: FeatureNode[], permission: PermissionConfig) => {
      const featureConfig = featureConfigs.find(
        (config: FeatureConfig) => config.id === permission.id,
      )
      if (featureConfig) {
        const featureNode = {
          permissionLevel: permission.level,
          parentId,
          ...featureConfig,
          ...(!!permission.children && {
            children: FeaturesTreeUtils.getFeatureNodesFromPermissions(
              permission.children,
              featureConfigs,
              permission.id,
            ),
          }),
        }
        if (
          (featureNode.permissionLevel &&
            featureNode.permissionLevel !== PermissionLevelEnum.NONE) ||
          featureNode.children?.length
        ) {
          featureNodes.push(featureNode)
        }
      }
      return featureNodes
    }, [])
  }
}
